import React, { useState, useEffect, useContext } from "react";
import { Card, Row, Col, Spinner, Badge, Modal, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { PageTitle } from "../../../_metronic/layout/core";
import { API_URL } from "../../config/api.config";
import axios from "axios";
import BedRockContext from "../../BedRockContext";
import { formatDateTime } from "../../utils/Utils";

// Format date for display
const formatDate = (dateString) => formatDateTime(dateString);

// Format relative time
const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch {
        return '';
    }
};

// Sync status dot component (for thumbnail overlay)
const SyncStatusDot = ({ status }) => {
    const color = status === 'UPLOADED' ? '#198754' : '#dc3545'; // green if synced, red otherwise
    return (
        <div
            style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: color,
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
            }}
        />
    );
};

// Sync status badge component (for modal details - full badge with label)
const SyncStatusBadge = ({ status }) => {
    const getStatusConfig = (status) => {
        switch (status) {
            case 'UPLOADED':
                return { bg: 'success', icon: 'bi-cloud-check', label: 'Synced' };
            case 'PENDING':
                return { bg: 'warning', icon: 'bi-cloud-arrow-up', label: 'Pending' };
            case 'UPLOADING':
                return { bg: 'info', icon: 'bi-cloud-upload', label: 'Uploading' };
            case 'FAILED':
                return { bg: 'danger', icon: 'bi-cloud-slash', label: 'Failed' };
            default:
                return { bg: 'secondary', icon: 'bi-cloud', label: status || 'Unknown' };
        }
    };

    const config = getStatusConfig(status);
    return (
        <Badge bg={config.bg} className="d-flex align-items-center gap-1" style={{ fontSize: '0.65rem' }}>
            <i className={`bi ${config.icon}`}></i>
            {config.label}
        </Badge>
    );
};

// Entry type badge component
const EntryTypeBadge = ({ type }) => {
    const isSale = type === 'SALE';
    return (
        <Badge
            bg={isSale ? 'primary' : 'info'}
            style={{ fontSize: '0.65rem', color: '#ffffff' }}
        >
            {isSale ? 'Sale' : 'Purchase'}
        </Badge>
    );
};

// Image type badge
const ImageTypeBadge = ({ type }) => {
    const isLoaded = type === 'LOADED_WEIGHT';
    return (
        <Badge
            bg="dark"
            className="bg-opacity-75"
            style={{ fontSize: '0.6rem', color: '#ffffff' }}
        >
            {isLoaded ? 'Loaded' : 'Empty'}
        </Badge>
    );
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
};

// Get date N days ago in YYYY-MM-DD format
const getDateDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
};

// Format date for display (e.g., "Feb 04, 2026")
const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
    });
};

// Get day name from date
const getDayName = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long' });
};

// Get display label for date (Today, Yesterday, or day name)
const getDateLabel = (dateString) => {
    const today = getTodayDate();
    const yesterday = getDateDaysAgo(1);

    if (dateString === today) return 'Today';
    if (dateString === yesterday) return 'Yesterday';

    // Check if within last 7 days - show day name
    for (let i = 2; i < 7; i++) {
        if (dateString === getDateDaysAgo(i)) {
            return getDayName(dateString);
        }
    }
    return formatDisplayDate(dateString);
};

// Generate quick select options for last 7 days
const getQuickSelectOptions = () => {
    const options = [];
    const dayIcons = [
        'bi-calendar-check',  // Today
        'bi-calendar-minus',  // Yesterday
        'bi-calendar-week',   // Other days
        'bi-calendar-week',
        'bi-calendar-week',
        'bi-calendar-week',
        'bi-calendar-week',
    ];

    for (let i = 0; i < 7; i++) {
        const date = getDateDaysAgo(i);
        let label;
        if (i === 0) label = 'Today';
        else if (i === 1) label = 'Yesterday';
        else label = getDayName(date);

        options.push({
            label,
            date,
            icon: dayIcons[i]
        });
    }
    return options;
};

// Date Picker Component
const DatePickerDropdown = ({ selectedDate, onDateChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const dropdownRef = React.useRef(null);

    // Quick select options - last 7 days
    const quickOptions = getQuickSelectOptions();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setShowCustom(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleQuickSelect = (date) => {
        onDateChange(date);
        setIsOpen(false);
        setShowCustom(false);
    };

    const handleCustomDateChange = (e) => {
        onDateChange(e.target.value);
        setIsOpen(false);
        setShowCustom(false);
    };

    const isSelected = (date) => selectedDate === date;

    return (
        <div className="position-relative" ref={dropdownRef}>
            {/* Main Button */}
            <button
                type="button"
                className={`btn btn-sm ${isOpen ? 'btn-primary' : 'btn-light-primary'} d-flex align-items-center gap-2 px-3`}
                onClick={() => setIsOpen(!isOpen)}
                style={{ minWidth: '180px' }}
            >
                <i className="bi bi-calendar3"></i>
                <span className="fw-semibold">{getDateLabel(selectedDate)}</span>
                <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-auto`} style={{ fontSize: '0.7rem' }}></i>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className="position-absolute end-0 mt-1 bg-white rounded shadow-lg border"
                    style={{
                        zIndex: 1050,
                        minWidth: '260px',
                        animation: 'fadeIn 0.15s ease-out'
                    }}
                >
                    {/* Quick Options */}
                    <div className="p-2">
                        <div className="text-muted small fw-semibold px-2 py-1 mb-1">Quick Select</div>
                        {quickOptions.map((option) => (
                            <div
                                key={option.date}
                                className={`d-flex align-items-center gap-2 px-3 py-2 rounded ${
                                    isSelected(option.date)
                                        ? 'bg-primary bg-opacity-10 text-primary'
                                        : 'hover-bg-light'
                                }`}
                                style={{ cursor: 'pointer' }}
                                role="button"
                                onClick={() => handleQuickSelect(option.date)}
                            >
                                <i className={`bi ${option.icon} ${isSelected(option.date) ? 'text-primary' : 'text-muted'}`}></i>
                                <span className="flex-grow-1">{option.label}</span>
                                {isSelected(option.date) && (
                                    <i className="bi bi-check2 text-primary"></i>
                                )}
                                <small className="text-muted">{formatDisplayDate(option.date)}</small>
                            </div>
                        ))}
                    </div>

                    <hr className="my-0" />

                    {/* Custom Date Section */}
                    <div className="p-2">
                        {!showCustom ? (
                            <div
                                className="d-flex align-items-center gap-2 px-3 py-2 rounded hover-bg-light"
                                style={{ cursor: 'pointer' }}
                                role="button"
                                onClick={() => setShowCustom(true)}
                            >
                                <i className="bi bi-calendar-week text-muted"></i>
                                <span>Pick a specific date...</span>
                            </div>
                        ) : (
                            <div className="px-2 py-1">
                                <div className="text-muted small fw-semibold mb-2">Select Date</div>
                                <Form.Control
                                    type="date"
                                    value={selectedDate}
                                    onChange={handleCustomDateChange}
                                    max={getTodayDate()}
                                    autoFocus
                                    className="form-control-sm"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Image thumbnail card component
const ImageThumbnail = ({ image, onClick }) => {
    const [imageError, setImageError] = useState(false);

    return (
        <div
            className="position-relative cursor-pointer"
            onClick={() => onClick(image)}
            style={{ cursor: 'pointer' }}
        >
            <div
                className="border rounded overflow-hidden position-relative"
                style={{
                    aspectRatio: '16/9',
                    backgroundColor: '#f8f9fa'
                }}
            >
                {!imageError ? (
                    <img
                        src={`${API_URL}/api/vehicle-capture/image/${image.primaryId}`}
                        alt={image.imageType || 'Captured image'}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                        }}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                        <i className="bi bi-image-alt fs-1"></i>
                    </div>
                )}

                {/* Top overlay - Sync status and Entry type */}
                <div
                    className="position-absolute top-0 start-0 end-0 d-flex justify-content-between align-items-start p-2"
                    style={{
                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)'
                    }}
                >
                    <SyncStatusDot status={image.imageSyncStatus} />
                    <EntryTypeBadge type={image.entryType} />
                </div>

                {/* Bottom overlay - Time and Image type */}
                <div
                    className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-between align-items-center p-2"
                    style={{
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)'
                    }}
                >
                    <span className="text-white" style={{ fontSize: '0.7rem' }}>
                        <i className="bi bi-clock me-1"></i>
                        {formatRelativeTime(image.capturedAt)}
                    </span>
                    <ImageTypeBadge type={image.imageType} />
                </div>
            </div>
        </div>
    );
};

// Image detail modal component
const ImageDetailModal = ({ show, image, onHide }) => {
    if (!image) return null;

    const InfoRow = ({ label, children }) => (
        <div className="d-flex justify-content-between align-items-center py-1 border-bottom">
            <span className="text-muted small">{label}</span>
            <span className="small">{children}</span>
        </div>
    );

    return (
        <Modal show={show} onHide={onHide} size="md" centered>
            <Modal.Header closeButton className="border-0 py-2">
                <Modal.Title className="fs-6">Image Details</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0">
                {/* Image */}
                <div className="mb-3">
                    <img
                        src={`${API_URL}/api/vehicle-capture/image/${image.primaryId}`}
                        alt={image.imageType || 'Captured image'}
                        className="rounded"
                        style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                    />
                </div>

                {/* Compact Info Table */}
                <div className="bg-light rounded p-2">
                    <InfoRow label="Type">
                        <EntryTypeBadge type={image.entryType} />
                        <span className="ms-2 text-muted">
                            {image.imageType === 'LOADED_WEIGHT' ? 'Loaded' : 'Empty'}
                        </span>
                    </InfoRow>
                    <InfoRow label="Entry">
                        {image.entryPrimaryId ? (
                            <Link
                                to={image.entryType === 'SALE'
                                    ? `/sale-new/view/${image.entryPrimaryId}`
                                    : `/purchase-new/view/${image.entryPrimaryId}`}
                                className="text-primary text-decoration-none"
                                onClick={onHide}
                            >
                                View {image.entryType === 'SALE' ? 'Sale' : 'Purchase'}
                                <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.65rem' }}></i>
                            </Link>
                        ) : '-'}
                    </InfoRow>
                    <InfoRow label="Captured">{formatDate(image.capturedAt)}</InfoRow>
                    {image.uploadedAt && (
                        <InfoRow label="Uploaded">{formatDate(image.uploadedAt)}</InfoRow>
                    )}
                    {image.fileSize && (
                        <InfoRow label="File Size">
                            {image.fileSize >= 1024 * 1024
                                ? `${(image.fileSize / (1024 * 1024)).toFixed(1)} MB`
                                : `${(image.fileSize / 1024).toFixed(1)} KB`}
                        </InfoRow>
                    )}
                    {image.contentType && (
                        <InfoRow label="Format">
                            {image.contentType.replace('image/', '').toUpperCase()}
                        </InfoRow>
                    )}
                    <InfoRow label="Status">
                        <SyncStatusBadge status={image.imageSyncStatus} />
                    </InfoRow>
                    {image.imageSyncStatus !== 'UPLOADED' && (
                        <InfoRow label="Attempts">{image.syncAttempts || 0}</InfoRow>
                    )}
                    {image.lastSyncError && (
                        <div className="mt-2 p-2 bg-danger bg-opacity-10 rounded">
                            <small className="text-danger" style={{ fontSize: '0.75rem' }}>
                                {image.lastSyncError}
                            </small>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 py-2">
                <a
                    href={`${API_URL}/api/vehicle-capture/image/${image.primaryId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm me-2"
                >
                    <i className="bi bi-box-arrow-up-right me-1"></i>
                    Open Full Size
                </a>
                <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

// Sync Progress Modal Component
const SyncProgressModal = ({ show, onHide, progress }) => {
    const getProgressPercent = () => {
        if (progress.status === 'completed') return 100;
        if (progress.status === 'error') return 0;
        if (progress.total === 0) return 0;
        return Math.round((progress.current / progress.total) * 100);
    };

    const getProgressBarVariant = () => {
        switch (progress.status) {
            case 'completed': return 'success';
            case 'error': return 'danger';
            default: return 'primary';
        }
    };

    const getStatusIcon = () => {
        switch (progress.status) {
            case 'completed': return <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }}></i>;
            case 'error': return <i className="bi bi-x-circle-fill text-danger" style={{ fontSize: '3rem' }}></i>;
            default: return <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />;
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered backdrop="static" keyboard={false}>
            <Modal.Header className="border-0 pb-0">
                <Modal.Title className="fs-5">
                    <i className="bi bi-cloud-upload me-2"></i>
                    Pushing Images to Server
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center py-4">
                <div className="mb-4">
                    {getStatusIcon()}
                </div>

                <div className="mb-3">
                    <div className="progress" style={{ height: '12px' }}>
                        <div
                            className={`progress-bar progress-bar-striped ${progress.status === 'syncing' ? 'progress-bar-animated' : ''} bg-${getProgressBarVariant()}`}
                            role="progressbar"
                            style={{ width: `${getProgressPercent()}%` }}
                            aria-valuenow={getProgressPercent()}
                            aria-valuemin="0"
                            aria-valuemax="100"
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-1">
                        <small className="text-muted">{progress.current} of {progress.total} images</small>
                        <small className="text-muted">{getProgressPercent()}%</small>
                    </div>
                </div>

                <p className={`mb-0 ${progress.status === 'error' ? 'text-danger' : progress.status === 'completed' ? 'text-success' : 'text-muted'}`}>
                    {progress.message}
                </p>
            </Modal.Body>
            {(progress.status === 'completed' || progress.status === 'error') && (
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button
                        variant={progress.status === 'completed' ? 'success' : 'secondary'}
                        onClick={onHide}
                    >
                        {progress.status === 'completed' ? 'Done' : 'Close'}
                    </Button>
                </Modal.Footer>
            )}
        </Modal>
    );
};

export function CapturedImagesPage() {
    const myData = useContext(BedRockContext);
    const [images, setImages] = useState([]);
    const [allTimeStats, setAllTimeStats] = useState({ total: 0, pending: 0, synced: 0, failed: 0, totalSize: 0, pendingSize: 0, syncedSize: 0, failedSize: 0, oldestDate: null, newestDate: null });
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, status: 'idle', message: '' });
    const [filter, setFilter] = useState('all'); // all, pending, synced, failed
    const [selectedDate, setSelectedDate] = useState(getTodayDate());

    const breadcrumbs = [
        {
            title: "Home",
            path: "/dashboard",
            isSeparator: false,
            isActive: false,
        },
        {
            title: "",
            path: "",
            isSeparator: true,
            isActive: false,
        },
    ];

    // Load all-time stats on mount
    useEffect(() => {
        loadAllTimeStats();
    }, []);

    // Load captured images when date changes
    useEffect(() => {
        loadImages();
    }, [selectedDate]);

    const loadAllTimeStats = async () => {
        setIsLoadingStats(true);
        try {
            const response = await axios.get(`${API_URL}/api/vehicle-capture/images/all`); // No date filter
            const data = response?.data;
            if (data?.success && data?.images) {
                const allImages = data.images;
                const pending = allImages.filter(i => i.imageSyncStatus === 'PENDING' || i.imageSyncStatus === 'UPLOADING');
                const synced = allImages.filter(i => i.imageSyncStatus === 'UPLOADED');
                const failed = allImages.filter(i => i.imageSyncStatus === 'FAILED');

                // Calculate storage sizes
                const totalSize = allImages.reduce((sum, img) => sum + (img.fileSize || 0), 0);
                const pendingSize = pending.reduce((sum, img) => sum + (img.fileSize || 0), 0);
                const syncedSize = synced.reduce((sum, img) => sum + (img.fileSize || 0), 0);
                const failedSize = failed.reduce((sum, img) => sum + (img.fileSize || 0), 0);

                // Find date range
                let oldestDate = null;
                let newestDate = null;
                if (allImages.length > 0) {
                    const dates = allImages.map(i => new Date(i.capturedAt)).filter(d => !isNaN(d));
                    if (dates.length > 0) {
                        oldestDate = new Date(Math.min(...dates));
                        newestDate = new Date(Math.max(...dates));
                    }
                }

                setAllTimeStats({
                    total: allImages.length,
                    pending: pending.length,
                    synced: synced.length,
                    failed: failed.length,
                    totalSize,
                    pendingSize,
                    syncedSize,
                    failedSize,
                    oldestDate,
                    newestDate
                });
            }
        } catch (error) {
            console.error("Error loading all-time stats:", error);
        } finally {
            setIsLoadingStats(false);
        }
    };

    const loadImages = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/vehicle-capture/images/all`, {
                params: { date: selectedDate }
            });
            const data = response?.data;
            if (data?.success && data?.images) {
                // Sort by capturedAt descending (newest first)
                const sortedImages = data.images.sort((a, b) =>
                    new Date(b.capturedAt) - new Date(a.capturedAt)
                );
                setImages(sortedImages);
            } else {
                setImages([]);
            }
        } catch (error) {
            console.error("Error loading images:", error);
            setImages([]);
        } finally {
            setIsLoading(false);
        }
    };

    const refreshAll = async () => {
        await Promise.all([loadImages(), loadAllTimeStats()]);
    };

    const handlePushToServer = async () => {
        const totalToSync = allTimeStats.pending + allTimeStats.failed;
        if (totalToSync === 0) return;

        setIsSyncing(true);
        setShowSyncModal(true);
        setSyncProgress({ current: 0, total: totalToSync, status: 'syncing', message: 'Starting sync...' });

        try {
            const response = await axios.post(`${API_URL}/api/vehicle-capture/images/sync`);
            const data = response?.data;

            if (data?.success) {
                const successCount = data.successCount || 0;
                const failedCount = data.failedCount || 0;

                setSyncProgress({
                    current: totalToSync,
                    total: totalToSync,
                    status: 'completed',
                    message: `Sync completed! ${successCount} uploaded${failedCount > 0 ? `, ${failedCount} failed` : ''}`
                });

                // Reload data
                await Promise.all([loadImages(), loadAllTimeStats()]);
            } else {
                setSyncProgress({
                    current: 0,
                    total: totalToSync,
                    status: 'error',
                    message: data?.error || 'Sync failed. Please try again.'
                });
            }
        } catch (error) {
            console.error("Error syncing images:", error);
            setSyncProgress({
                current: 0,
                total: totalToSync,
                status: 'error',
                message: 'Connection error. Please check your network and try again.'
            });
        } finally {
            setIsSyncing(false);
        }
    };

    const handleForceUpload = async () => {
        const totalToSync = allTimeStats.failed;
        if (totalToSync === 0) return;

        setIsSyncing(true);
        setShowSyncModal(true);
        setSyncProgress({ current: 0, total: totalToSync, status: 'syncing', message: 'Force uploading failed images...' });

        try {
            const response = await axios.post(`${API_URL}/api/vehicle-capture/images/force-sync`);
            const data = response?.data;

            if (data?.success) {
                const successCount = data.successCount || 0;
                const failedCount = data.failedCount || 0;
                const resetCount = data.resetCount || 0;

                setSyncProgress({
                    current: totalToSync,
                    total: totalToSync,
                    status: 'completed',
                    message: `Force upload complete! ${successCount} uploaded${failedCount > 0 ? `, ${failedCount} still failing` : ''}${resetCount > 0 ? ` (${resetCount} re-queued)` : ''}`
                });

                // Reload data
                await Promise.all([loadImages(), loadAllTimeStats()]);
            } else {
                setSyncProgress({
                    current: 0,
                    total: totalToSync,
                    status: 'error',
                    message: data?.error || data?.message || 'Force upload failed. Please try again.'
                });
            }
        } catch (error) {
            console.error("Error force uploading images:", error);
            setSyncProgress({
                current: 0,
                total: totalToSync,
                status: 'error',
                message: 'Connection error. Please check your network and try again.'
            });
        } finally {
            setIsSyncing(false);
        }
    };

    const closeSyncModal = () => {
        setShowSyncModal(false);
        setSyncProgress({ current: 0, total: 0, status: 'idle', message: '' });
    };

    const handleImageClick = (image) => {
        setSelectedImage(image);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedImage(null);
    };

    // Filter images based on selected filter
    const filteredImages = images.filter(image => {
        switch (filter) {
            case 'pending':
                return image.imageSyncStatus === 'PENDING' || image.imageSyncStatus === 'UPLOADING';
            case 'synced':
                return image.imageSyncStatus === 'UPLOADED';
            case 'failed':
                return image.imageSyncStatus === 'FAILED';
            default:
                return true;
        }
    });

    // Calculate stats for selected date
    const todayStats = {
        total: images.length,
        pending: images.filter(i => i.imageSyncStatus === 'PENDING' || i.imageSyncStatus === 'UPLOADING').length,
        synced: images.filter(i => i.imageSyncStatus === 'UPLOADED').length,
        failed: images.filter(i => i.imageSyncStatus === 'FAILED').length,
    };
    const unsyncedCount = allTimeStats.pending + allTimeStats.failed;

    // Set up toolbar content and cleanup on unmount
    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [],
            toolBarContent: (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                        onClick={handlePushToServer}
                        disabled={isSyncing || unsyncedCount === 0}
                    >
                        <i className={`bi bi-cloud-upload ${isSyncing ? 'spin' : ''}`}></i>
                        <span>{isSyncing ? 'Syncing...' : 'Push Images to Server'}</span>
                        {unsyncedCount > 0 && !isSyncing && (
                            <Badge bg="light" text="dark" className="ms-1">{unsyncedCount}</Badge>
                        )}
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-light-danger d-flex align-items-center gap-1"
                        onClick={handleForceUpload}
                        disabled={isSyncing || allTimeStats.failed === 0}
                        title="Reset and retry all failed images, even those that exhausted their retry limit"
                    >
                        <i className={`bi bi-arrow-repeat ${isSyncing ? 'spin' : ''}`}></i>
                        <span>Force Upload</span>
                        {allTimeStats.failed > 0 && !isSyncing && (
                            <Badge bg="light" text="dark" className="ms-1">{allTimeStats.failed}</Badge>
                        )}
                    </button>
                    <DatePickerDropdown
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-icon btn-light-primary"
                        onClick={refreshAll}
                        disabled={isLoading || isLoadingStats}
                        title="Refresh"
                    >
                        <i className={`bi bi-arrow-clockwise ${isLoading || isLoadingStats ? 'spin' : ''}`}></i>
                    </button>
                </div>
            ),
        });

        // Cleanup on unmount
        return () => {
            myData.setBaseData({
                toolBarButtons: [],
                toolBarContent: null,
            });
        };
    }, [selectedDate, isLoading, isLoadingStats, isSyncing, unsyncedCount, allTimeStats.failed]);

    // Format date range for display
    const formatDateRange = () => {
        if (!allTimeStats.oldestDate || !allTimeStats.newestDate) return '';
        const oldest = allTimeStats.oldestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const newest = allTimeStats.newestDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${oldest} - ${newest}`;
    };

    // Format file size for display
    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0) + ' ' + units[i];
    };

    // Calculate today's storage size
    const todaySize = images.reduce((sum, img) => sum + (img.fileSize || 0), 0);

    return (
        <>
            <PageTitle breadcrumbs={breadcrumbs}>Captured Images</PageTitle>

            {/* Stats Cards - All Time Stats */}
            <Row className="g-3 mb-4">
                <Col xs={6} md={3}>
                    <Card
                        className={`border-0 shadow-sm overflow-hidden h-100 ${filter === 'all' ? 'ring-2 ring-primary' : ''}`}
                        onClick={() => setFilter('all')}
                        style={{ cursor: 'pointer', borderLeft: '4px solid #6366f1' }}
                    >
                        <Card.Body className="p-3 position-relative">
                            <svg className="position-absolute" style={{ bottom: -20, right: -20, opacity: 0.06 }} width="100" height="100" viewBox="0 0 100 100">
                                <rect x="20" y="10" width="60" height="80" rx="8" fill="#6366f1"/>
                                <circle cx="50" cy="35" r="15" fill="#6366f1"/>
                                <rect x="30" y="55" width="40" height="6" rx="3" fill="#6366f1"/>
                                <rect x="35" y="67" width="30" height="6" rx="3" fill="#6366f1"/>
                            </svg>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                         style={{ width: 44, height: 44, backgroundColor: '#eef2ff' }}>
                                        <i className="bi bi-images" style={{ fontSize: '1.1rem', color: '#6366f1' }}></i>
                                    </div>
                                    <div>
                                        <div className="fs-3 fw-bold lh-1" style={{ color: '#6366f1' }}>
                                            {isLoadingStats ? '-' : allTimeStats.total}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.8rem' }}>Total Images</small>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-semibold fs-5" style={{ color: '#6366f1' }}>{todayStats.total}</div>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>{getDateLabel(selectedDate)}</small>
                                </div>
                            </div>
                            {/* Storage Info */}
                            <div className="mt-2 pt-2 border-top">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-hdd-fill me-1" style={{ color: '#6366f1', fontSize: '1rem' }}></i>
                                            <span className="fw-bold" style={{ color: '#6366f1', fontSize: '1.1rem' }}>
                                                {isLoadingStats ? '-' : formatFileSize(allTimeStats.totalSize)}
                                            </span>
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>Total on Disk</small>
                                    </div>
                                    <div className="text-end">
                                        <span className="fw-semibold" style={{ color: '#6366f1', fontSize: '1rem' }}>
                                            {formatFileSize(todaySize)}
                                        </span>
                                        <div>
                                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>{getDateLabel(selectedDate)}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {allTimeStats.total > 0 && (
                                <div className="mt-1 pt-1 border-top">
                                    <small className="text-muted d-flex align-items-center" style={{ fontSize: '0.75rem' }}>
                                        <i className="bi bi-calendar-range me-1"></i>
                                        {formatDateRange()}
                                    </small>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card
                        className={`border-0 shadow-sm overflow-hidden h-100 ${filter === 'pending' ? 'ring-2 ring-warning' : ''}`}
                        onClick={() => setFilter('pending')}
                        style={{ cursor: 'pointer', borderLeft: '4px solid #f59e0b' }}
                    >
                        <Card.Body className="p-3 position-relative">
                            <svg className="position-absolute" style={{ bottom: -20, right: -20, opacity: 0.06 }} width="100" height="100" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="#f59e0b"/>
                                <circle cx="50" cy="50" r="30" fill="white"/>
                                <path d="M50 25 V50 H70" fill="none" stroke="#f59e0b" strokeWidth="6" strokeLinecap="round"/>
                            </svg>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                         style={{ width: 44, height: 44, backgroundColor: '#fef3c7' }}>
                                        <i className="bi bi-clock-history" style={{ fontSize: '1.1rem', color: '#f59e0b' }}></i>
                                    </div>
                                    <div>
                                        <div className="fs-3 fw-bold lh-1" style={{ color: '#f59e0b' }}>
                                            {isLoadingStats ? '-' : allTimeStats.pending}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.8rem' }}>Pending</small>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-semibold fs-5" style={{ color: '#f59e0b' }}>{todayStats.pending}</div>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>{getDateLabel(selectedDate)}</small>
                                </div>
                            </div>
                            {allTimeStats.pending > 0 && (
                                <div className="mt-2 pt-2 border-top">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-warning d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                                            <i className="bi bi-cloud-arrow-up me-1"></i>
                                            To sync
                                        </span>
                                        <span className="fw-bold" style={{ color: '#f59e0b', fontSize: '1rem' }}>
                                            {formatFileSize(allTimeStats.pendingSize)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card
                        className={`border-0 shadow-sm overflow-hidden h-100 ${filter === 'synced' ? 'ring-2 ring-success' : ''}`}
                        onClick={() => setFilter('synced')}
                        style={{ cursor: 'pointer', borderLeft: '4px solid #22c55e' }}
                    >
                        <Card.Body className="p-3 position-relative">
                            <svg className="position-absolute" style={{ bottom: -20, right: -20, opacity: 0.06 }} width="100" height="100" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="#22c55e"/>
                                <path d="M30 50 L45 65 L75 30" fill="none" stroke="white" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                         style={{ width: 44, height: 44, backgroundColor: '#dcfce7' }}>
                                        <i className="bi bi-cloud-check" style={{ fontSize: '1.1rem', color: '#22c55e' }}></i>
                                    </div>
                                    <div>
                                        <div className="fs-3 fw-bold lh-1" style={{ color: '#22c55e' }}>
                                            {isLoadingStats ? '-' : allTimeStats.synced}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.8rem' }}>Synced</small>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-semibold fs-5" style={{ color: '#22c55e' }}>{todayStats.synced}</div>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>{getDateLabel(selectedDate)}</small>
                                </div>
                            </div>
                            {allTimeStats.synced > 0 && (
                                <div className="mt-2 pt-2 border-top">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            {allTimeStats.total > 0 ? Math.round((allTimeStats.synced / allTimeStats.total) * 100) : 0}% synced
                                        </span>
                                        <span className="fw-bold" style={{ color: '#22c55e', fontSize: '1rem' }}>
                                            {formatFileSize(allTimeStats.syncedSize)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={6} md={3}>
                    <Card
                        className={`border-0 shadow-sm overflow-hidden h-100 ${filter === 'failed' ? 'ring-2 ring-danger' : ''}`}
                        onClick={() => setFilter('failed')}
                        style={{ cursor: 'pointer', borderLeft: '4px solid #ef4444' }}
                    >
                        <Card.Body className="p-3 position-relative">
                            <svg className="position-absolute" style={{ bottom: -20, right: -20, opacity: 0.06 }} width="100" height="100" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="#ef4444"/>
                                <path d="M35 35 L65 65 M65 35 L35 65" fill="none" stroke="white" strokeWidth="10" strokeLinecap="round"/>
                            </svg>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                                         style={{ width: 44, height: 44, backgroundColor: '#fee2e2' }}>
                                        <i className="bi bi-exclamation-triangle" style={{ fontSize: '1.1rem', color: '#ef4444' }}></i>
                                    </div>
                                    <div>
                                        <div className="fs-3 fw-bold lh-1" style={{ color: '#ef4444' }}>
                                            {isLoadingStats ? '-' : allTimeStats.failed}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.8rem' }}>Failed</small>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <div className="fw-semibold fs-5" style={{ color: '#ef4444' }}>{todayStats.failed}</div>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>{getDateLabel(selectedDate)}</small>
                                </div>
                            </div>
                            {allTimeStats.failed > 0 && (
                                <div className="mt-2 pt-2 border-top">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-danger d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                                            <i className="bi bi-arrow-repeat me-1"></i>
                                            Retry sync
                                        </span>
                                        <span className="fw-bold" style={{ color: '#ef4444', fontSize: '1rem' }}>
                                            {formatFileSize(allTimeStats.failedSize)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Filter Info */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    {filter !== 'all' && (
                        <Badge bg="secondary" className="me-2">
                            Showing: {filter.charAt(0).toUpperCase() + filter.slice(1)}
                            <i
                                className="bi bi-x-circle ms-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setFilter('all')}
                            ></i>
                        </Badge>
                    )}
                    <small className="text-muted">{filteredImages.length} images for {formatDisplayDate(selectedDate)}</small>
                </div>
            </div>

            {/* Image Gallery */}
            {isLoading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2">Loading images...</p>
                </div>
            ) : filteredImages.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <i className="bi bi-images text-muted" style={{ fontSize: '4rem', opacity: 0.3 }}></i>
                        <h5 className="text-muted mt-3">No Images Found</h5>
                        <p className="text-muted mb-0">
                            {filter === 'all'
                                ? 'Captured images will appear here'
                                : `No ${filter} images found`}
                        </p>
                    </Card.Body>
                </Card>
            ) : (
                <Row className="g-3">
                    {filteredImages.map((image, index) => (
                        <Col xs={12} sm={6} md={4} lg={3} key={image.primaryId || index}>
                            <ImageThumbnail
                                image={image}
                                onClick={handleImageClick}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            {/* Info Message */}
            <div className="mt-4 p-3 bg-light rounded d-flex align-items-center">
                <i className="bi bi-info-circle text-muted me-2"></i>
                <small className="text-muted">
                    Synced images are automatically deleted from this device after 2 days to free up local storage.
                    They will remain available on the server.
                </small>
            </div>

            {/* Image Detail Modal */}
            <ImageDetailModal
                show={showModal}
                image={selectedImage}
                onHide={handleCloseModal}
            />

            {/* Sync Progress Modal */}
            <SyncProgressModal
                show={showSyncModal}
                onHide={closeSyncModal}
                progress={syncProgress}
            />

            {/* CSS for animations and hover effects */}
            <style>{`
                .spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .hover-bg-light:hover {
                    background-color: #f8f9fa;
                    cursor: pointer;
                }
                [role="button"] {
                    cursor: pointer !important;
                }
                input[type="date"] {
                    cursor: pointer;
                }
                input[type="date"]::-webkit-calendar-picker-indicator {
                    cursor: pointer;
                }
            `}</style>
        </>
    );
}
